import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listDisputes } from "@/services/disputes.service";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { fmtMoney, fmtDateTime, statusColor } from "@/lib/format";
import { Plus, AlertTriangle, ArrowRight } from "lucide-react";
import { FileDisputeModal } from "@/components/disputes/FileDisputeModal";
import DisputeDetail from "@/components/disputes/DisputeDetail";
import { usePolling } from "@/lib/usePolling";

const Disputes = () => {
  const { user } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fileOpen, setFileOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    try { setDisputes(await listDisputes()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  usePolling(load);

  const filtered = useMemo(() => {
    return disputes.filter((d) => statusFilter === "all" || d.status === statusFilter);
  }, [disputes, statusFilter]);

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="disputes-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Disputes</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Disputes</h1>
          <p className="mt-2 text-sm text-muted-foreground">File a dispute on any funded milestone. Releases are blocked until resolved.</p>
        </div>
        {!isAdmin && (
          <Button onClick={() => setFileOpen(true)} data-testid="file-dispute-btn">
            <Plus className="w-4 h-4 mr-1.5" /> File a dispute
          </Button>
        )}
      </div>

      <div className="inline-flex items-center gap-1 bg-secondary/50 border border-border rounded-lg p-1">
        {[["all", "All"], ["Open", "Open"], ["UnderReview", "Under review"], ["Resolved", "Resolved"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setStatusFilter(k)} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${statusFilter === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`dispute-filter-${k}`}>
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto text-muted-foreground/60" />
          <div className="mt-3 font-medium">No disputes{statusFilter !== "all" ? ` with status ${statusFilter}` : ""}.</div>
          <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">Disputes can only be opened on <b>funded</b> milestones — once money is in escrow, either party can pause release until it's resolved.</p>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            {!isAdmin && (
              <Button variant="outline" onClick={() => setFileOpen(true)} data-testid="empty-file-dispute-btn">
                <Plus className="w-4 h-4 mr-1.5" /> File a dispute
              </Button>
            )}
            <Link to="/contracts">
              <Button variant="ghost" data-testid="dispute-empty-contracts-cta">Go to contracts</Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-muted-foreground text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Dispute ID</th>
                <th className="text-left px-4 py-3 font-semibold">Contract</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Milestone</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold hidden sm:table-cell">Created</th>
                <th className="text-right px-4 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((d) => (
                <tr key={d.dispute_id} className="hover:bg-secondary/30" data-testid={`dispute-row-${d.dispute_id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{d.dispute_id}</td>
                  <td className="px-4 py-3">
                    <Link to={`/contracts/${d.contract_id}`} className="font-medium hover:text-primary" data-testid={`dispute-contract-link-${d.dispute_id}`}>{d.contract_title || "—"}</Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{d.milestone_title || "—"}</td>
                  <td className="px-4 py-3 text-right font-display font-semibold">{fmtMoney(d.amount)}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor(d.status)}`}>{d.status === "UnderReview" ? "Under review" : d.status}</span></td>
                  <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{fmtDateTime(d.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setDetailId(d.dispute_id)} className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1" data-testid={`view-dispute-${d.dispute_id}`}>
                      View <ArrowRight className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FileDisputeModal open={fileOpen} onClose={() => setFileOpen(false)} onFiled={load} />
      {detailId && <DisputeDetail disputeId={detailId} onClose={() => setDetailId(null)} onResolved={load} />}
    </div>
  );
};

export default Disputes;
